import React, { useCallback } from "react";

import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Chip,
  ListItem,
  ListItemText,
  Theme,
  Typography,
} from "@mui/material";
import { intersection } from "lodash";
import { makeStyles, withStyles } from "tss-react/mui";

import { AllergiesKind, FoodItemFragment } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";

interface Props {
  food: FoodItemFragment;
  onSelect: (food: FoodItemFragment) => void;
  clientAllergies?: AllergiesKind[];
}

const useStyles = makeStyles()(() => ({
  secondaryContent: {
    flex: 1,
  },
  textPrimary: {
    fontSize: 16,
    lineHeight: "22px",
    fontWeight: 600,
    color: colors.dark,
  },
  textSecondary: {
    display: "flex",
    alignItems: "center",
    fontSize: 14,
    lineHeight: "20px",
    fontWeight: 600,
    color: colors.grey,
  },
}));

const StyledListItem = withStyles(ListItem, (theme: Theme) => ({
  root: {
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    "&:hover": {
      backgroundColor: theme.palette.grey[100],
    },
  },
}));

const StyledChip = withStyles(Chip, (theme) => ({
  root: {
    backgroundColor: "#FF6262",
    color: colors.white,
    maxHeight: 21,
    fontSize: theme.typography.pxToRem(10),
  },
}));

const StyledWarningIcon = withStyles(WarningIcon, {
  root: {
    maxHeight: 12,
    maxWidth: 12,
  },
});

const FoodSelectItem: React.FC<Props> = ({
  food,
  onSelect,
  clientAllergies = [],
}) => {
  const { classes } = useStyles();

  const hasAllergies =
    intersection(food.allergies, clientAllergies).length !== 0;

  const handleClick = useCallback(() => {
    onSelect(food);
  }, [onSelect]);
  return (
    <StyledListItem button onClick={handleClick}>
      <ListItemText
        // primary={food.name}
        primary={
          <Box display="flex" justifyContent="space-between" marginRight={4}>
            <Typography variant="body1" color="textPrimary">
              {food.name}
            </Typography>
            {hasAllergies && (
              <StyledChip
                icon={<StyledWarningIcon style={{ color: colors.white }} />}
                label="Allergen"
              />
            )}
          </Box>
        }
        secondary={
          <span className={classes.secondaryContent}>
            <Box
              display="flex"
              flex={1}
              justifyContent="space-between"
              component="span"
            >
              <Box flex={2} component="span">
                <span>{food.source}</span>
              </Box>
              <Box
                flex={2}
                display="flex"
                component="span"
                justifyContent="space-between"
              >
                <Box marginRight={2}>
                  <Typography color="textSecondary">
                    C: {macronutrientValue(food.carbohydrateGrams)}
                  </Typography>
                </Box>
                <Box marginRight={2}>
                  <Typography color="textSecondary">
                    P: {macronutrientValue(food.proteinGrams)}
                  </Typography>
                </Box>
                <Box marginRight={2}>
                  <Typography color="textSecondary">
                    F: {macronutrientValue(food.fatGrams)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </span>
        }
        classes={{
          primary: classes.textPrimary,
          secondary: classes.textSecondary,
        }}
      />
    </StyledListItem>
  );
};

function macronutrientValue(macro: number | null | undefined) {
  if (macro == null) return "N/A";
  return `${macro}g`;
}

export default FoodSelectItem;
